import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, MapPin, ChevronRight, CalendarClock } from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import { EVENTS as TID } from '@/constants/testIds';
import { buildAssetUrl } from '@/lib/api';
import { formatEventDate } from '@/components/events/EventStatusBadge';

export function EventCard({ event }) {
  const cover = buildAssetUrl(event.thumbnail_url);
  const teamsLine = [event.team_home?.name, event.team_away?.name]
    .filter(Boolean)
    .join(' vs ');
  const locationLine = [event.city, event.province].filter(Boolean).join(', ');

  return (
    <Card
      data-testid={TID.card(event.slug)}
      className="group overflow-hidden border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 hover:shadow-[0_12px_36px_rgba(28,22,18,0.08)] transition-all duration-200 rounded-2xl bg-white"
    >
      <Link to={ROUTES.eventDetail(event.slug)} className="block">
        <div className="relative h-40 sm:h-44 bg-gradient-to-br from-[var(--ns-gold)]/25 to-[var(--ns-gold-700)]/20 overflow-hidden">
          {cover ? (
            <img
              src={cover}
              alt={event.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              draggable={false}
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
          {/* Fase Eventos v2 · Ya no se muestran las badges de estado ni de
              tipo de acceso encima de la miniatura. El acceso sigue
              funcionando internamente igual; solo se retira su badge visual. */}
          {event.has_download_product ? (
            <div className="absolute top-3 right-3">
              <Badge
                data-testid={`event-card-download-badge-${event.slug}`}
                className="bg-[var(--ns-gold)]/95 text-[#1c1612] border-0 text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-full shadow-sm"
              >
                <Download className="h-3 w-3" />
                Descarga
              </Badge>
            </div>
          ) : null}
          {event.sport_name ? (
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-white/95 bg-black/35 backdrop-blur-sm px-2 py-1 rounded-full">
              {event.sport_name}
              {event.category ? <span className="text-white/70">· {event.category}</span> : null}
            </div>
          ) : null}
        </div>
        <div className="p-4 sm:p-5">
          <h3 className="font-display text-base sm:text-lg leading-tight text-[var(--ns-text)] line-clamp-2">
            {event.title}
          </h3>
          {teamsLine ? (
            <p className="mt-1 text-xs text-[var(--ns-text-muted)] truncate">{teamsLine}</p>
          ) : null}
          <div className="mt-3 flex flex-col gap-1.5 text-xs text-[var(--ns-text-muted)]">
            {event.event_date ? (
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-[var(--ns-gold-600)] shrink-0" />
                {formatEventDate(event.event_date)}
              </span>
            ) : null}
            {locationLine ? (
              <span className="inline-flex items-center gap-1.5 truncate">
                <MapPin className="h-3.5 w-3.5 text-[var(--ns-gold-600)] shrink-0" />
                <span className="truncate">{locationLine}</span>
              </span>
            ) : null}
          </div>
          <div className="mt-4 flex items-center justify-between">
            {event.club_name ? (
              <span className="text-xs text-[var(--ns-text-subtle)] truncate max-w-[60%]">
                {event.club_name}
              </span>
            ) : <span />}
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-[var(--ns-gold-700)] hover:text-[#1c1612] hover:bg-[var(--ns-gold)]/20"
              data-testid={TID.cardCta(event.slug)}
            >
              <span className="inline-flex items-center gap-1 font-semibold">
                Ver evento
                <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </Button>
          </div>
        </div>
      </Link>
    </Card>
  );
}

export function EventCardSkeleton() {
  return (
    <div className="rounded-2xl border border-[var(--ns-border)] bg-white overflow-hidden">
      <div className="h-40 sm:h-44 bg-[var(--ns-bg-2)] animate-pulse" />
      <div className="p-4 sm:p-5 space-y-2">
        <div className="h-4 w-4/5 bg-[var(--ns-bg-2)] rounded animate-pulse" />
        <div className="h-3 w-3/5 bg-[var(--ns-bg-2)] rounded animate-pulse" />
        <div className="h-3 w-2/5 bg-[var(--ns-bg-2)] rounded animate-pulse" />
      </div>
    </div>
  );
}
